import React, { useEffect, useState } from 'react';
import {
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  View,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';

import { createContentBlock, updateContentBlock } from '../../../api/Settings';
import BasicModalContainer from '../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../common/components/basicmodal/ModalFooter';
import Select from '../../../common/components/ui/Select';
import { msgStr } from '../../../common/constants/Message';
import { useAlertModal } from '../../../common/hooks';
import { FieldType, Setting } from '../storedetails/brandContent/ContentField';

const FIELD_TYPE_OPTIONS: { label: string; value: FieldType }[] = [
  { label: 'Text', value: 'text' },
  { label: 'Textarea', value: 'textarea' },
  { label: 'HTML', value: 'html' },
  { label: 'Image', value: 'image' },
  { label: 'Link', value: 'link' },
  { label: 'JSON', value: 'json' },
  { label: 'Color', value: 'color' },
  { label: 'Multiselect', value: 'multiselect' },
  { label: 'Select', value: 'select' },
  { label: 'Checkbox', value: 'checkbox' },
];

const ACTIVE_OPTIONS = [
  { label: 'Active', value: '1' },
  { label: 'Inactive', value: '0' },
];

export interface ContentBlock {
  id?: number;
  key_name: string;
  group_name: string | null;
  sort_order: number;
  label: string;
  description: string | null;
  field_type: FieldType;
  is_active: 0 | 1;
}

interface Props {
  isModalVisible: boolean;
  closeModal: () => void;
  onSaved: () => void;
  block?: ContentBlock | Setting | null;
}

const emptyForm: ContentBlock = {
  key_name: '',
  group_name: '',
  sort_order: 0,
  label: '',
  description: '',
  field_type: 'text',
  is_active: 1,
};

const ContentBlockModal = ({ isModalVisible, closeModal, onSaved, block }: Props) => {
  const { showAlert } = useAlertModal() as unknown as {
    showAlert: (type: string, message: string) => void;
  };
  const [form, setForm] = useState<ContentBlock>(emptyForm);
  const [validMessage, setValidMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isEdit = !!block?.id;

  useEffect(() => {
    if (block) {
      setForm({
        id: block.id,
        key_name: block.key_name || '',
        group_name: block.group_name || '',
        sort_order: block.sort_order ?? 0,
        label: block.label || '',
        description: block.description || '',
        field_type: (block.field_type as FieldType) || 'text',
        is_active: (block.is_active ?? 1) as 0 | 1,
      });
    } else {
      setForm(emptyForm);
    }
    setValidMessage('');
  }, [block, isModalVisible]);

  const setField = <K extends keyof ContentBlock>(key: K, value: ContentBlock[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    if (!form.label.trim() || !form.key_name.trim()) {
      setValidMessage(msgStr('emptyField'));
      return;
    }
    setValidMessage('');
    setIsLoading(true);

    const payload = {
      ...form,
      group_name: form.group_name?.trim() ? form.group_name.trim() : null,
      description: form.description?.trim() ? form.description.trim() : null,
      sort_order: Number(form.sort_order) || 0,
    };

    const handleResponse = (jsonRes: any, status: number) => {
      switch (status) {
        case 200:
        case 201:
          showAlert('success', jsonRes?.message || 'Content block saved successfully');
          onSaved();
          closeModal();
          break;
        case 409:
          setValidMessage(jsonRes?.error || 'A content block with this key already exists');
          break;
        default:
          showAlert('error', jsonRes?.error || msgStr('unknownError'));
          break;
      }
      setIsLoading(false);
    };

    if (isEdit) {
      updateContentBlock(payload, handleResponse);
    } else {
      createContentBlock(payload, handleResponse);
    }
  };

  return (
    <Modal animationType="none" transparent visible={isModalVisible}>
      <BasicModalContainer>
        <ModalHeader
          label={isEdit ? 'Edit Content Block' : 'Add Content Block'}
          closeModal={closeModal}
        />
        <ModalBody style={styles.body}>
          <Text style={styles.label}>Label *</Text>
          <TextInput
            style={styles.input}
            value={form.label}
            onChangeText={(v) => setField('label', v)}
            placeholder="e.g. Hero Title"
            placeholderTextColor="#ccc"
          />

          <Text style={styles.label}>Key Name *</Text>
          <TextInput
            style={[styles.input, isEdit && styles.inputDisabled]}
            value={form.key_name}
            onChangeText={(v) => setField('key_name', v)}
            placeholder="e.g. homepage.hero_title"
            placeholderTextColor="#ccc"
            editable={!isEdit}
          />

          <Select
            label="Field Type"
            options={FIELD_TYPE_OPTIONS}
            value={form.field_type}
            onChange={(v) => setField('field_type', v as FieldType)}
          />

          <Text style={styles.label}>Group Name</Text>
          <TextInput
            style={styles.input}
            value={form.group_name || ''}
            onChangeText={(v) => setField('group_name', v)}
            placeholder="e.g. homepage"
            placeholderTextColor="#ccc"
          />

          <Text style={styles.label}>Description</Text>
          <TextInput
            style={styles.input}
            value={form.description || ''}
            onChangeText={(v) => setField('description', v)}
            placeholder="Optional description"
            placeholderTextColor="#ccc"
          />

          <Text style={styles.label}>Sort Order</Text>
          <TextInput
            style={styles.input}
            value={String(form.sort_order)}
            onChangeText={(v) => setField('sort_order', (v.replace(/[^0-9]/g, '') as unknown) as number)}
            placeholder="0"
            placeholderTextColor="#ccc"
            keyboardType="numeric"
          />

          <Select
            label="Status"
            options={ACTIVE_OPTIONS}
            value={String(form.is_active)}
            onChange={(v) => setField('is_active', (Number(v) as 0 | 1))}
          />

          {validMessage.trim() !== '' && <Text style={styles.message}>{validMessage}</Text>}
        </ModalBody>
        <ModalFooter>
          <TouchableOpacity onPress={handleSave}>
            <Text style={styles.addButton}>{isEdit ? 'Update' : 'Add'}</Text>
          </TouchableOpacity>
        </ModalFooter>
      </BasicModalContainer>
      {isLoading && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      )}
    </Modal>
  );
};

const styles = StyleSheet.create({
  body: {
    width: 360,
  },
  label: {
    fontSize: 13,
    color: '#555',
    marginTop: 6,
    marginBottom: 2,
  },
  input: {
    height: 40,
    borderColor: 'gray',
    borderWidth: 1,
    marginBottom: 6,
    padding: 8,
  },
  inputDisabled: {
    backgroundColor: '#f2f2f2',
    color: '#888',
  },
  addButton: {
    backgroundColor: '#2e96e1',
    color: 'white',
    paddingVertical: 10,
    paddingHorizontal: 24,
    textAlign: 'center',
    borderRadius: 5,
  },
  message: {
    color: 'red',
    fontSize: 13,
    marginTop: 4,
  },
  overlay: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
  },
});

export default ContentBlockModal;
